import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { monsterApi, spellApi, spriteApi } from '../../../api/adminApi';
import { useTranslation } from '../../../i18n/useTranslation';
import type { AIBehavior, MonsterTemplate, SpellData } from '../../../types/adminTypes';
import { MONSTER_DEFAULTS } from '../../../types/adminTypes';

// ── Monster randomiser ────────────────────────────────────────────────────────
const _TITLES  = ['Ancient', 'Dark', 'Shadow', 'Blood', 'Iron', 'Plague', 'Frost', 'Void', 'Cursed', 'Storm', 'Vile', 'Bone'] as const;
const _BASES   = ['Goblin', 'Orc', 'Troll', 'Wolf', 'Drake', 'Golem', 'Shade', 'Fiend', 'Wraith', 'Ogre', 'Lich', 'Bandit'] as const;
const _ROLES   = ['Pillager', 'Warden', 'Stalker', 'Shaman', 'Colossus', 'Reaper', 'Sentinel', 'Hunter', 'Knight', 'Mage', 'Guard', 'Elder'] as const;
const _AI: AIBehavior[] = ['aggressive', 'defensive', 'support'];

function randomizeMonster(availableSpells: SpellData[], availableSprites: any[]): Omit<MonsterTemplate, 'id' | 'created_at' | 'updated_at'> {
  const ri   = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
  const pick = <T,>(arr: readonly T[]): T  => arr[Math.floor(Math.random() * arr.length)];

  const fmt   = ri(0, 2);
  const title = pick(_TITLES);
  const base  = pick(_BASES);
  const role  = pick(_ROLES);
  const name  = fmt === 0 ? `${title} ${base}` : fmt === 1 ? `${base} ${role}` : `${title} ${base} ${role}`;

  const level    = ri(1, 20);
  const scale    = 1 + (level - 1) * 0.12;
  const behavior = pick(_AI);
  const isAgg    = behavior === 'aggressive';
  const isDef    = behavior === 'defensive';
  const isSup    = behavior === 'support';

  // Pick 0-3 random spells
  const shuffled  = [...availableSpells].sort(() => Math.random() - 0.5);
  const spell_ids = shuffled.slice(0, ri(0, Math.min(3, availableSpells.length))).map((s) => s.id);

  const monsterSprites = availableSprites.filter(s => s.type === 'monster');
  const sprite = monsterSprites.length > 0 ? monsterSprites[Math.floor(Math.random() * monsterSprites.length)].name : '';

  return {
    name,
    description: '',
    icon_url:    '',
    level,
    xp_reward:      level * ri(20, 60),
    // Monstros gerados aleatoriamente não possuem bônus de ranking individual.
    // O admin pode ajustar manualmente após a criação caso seja um boss.
    ranking_points: 0,
    hp_max:         Math.round(ri(isAgg ? 40 : 70, isAgg ? 130 : 220) * scale),
    ap_max:         ri(isAgg ? 6 : 4, isAgg ? 12 : 8),
    mp_max:         isSup ? ri(4, 8) : ri(0, 5),
    strength:       isAgg ? ri(20, 80) : ri(0, 40),
    intelligence:   isSup ? ri(20, 80) : ri(0, 40),
    agility:        ri(0, 60),
    luck:           ri(0, 40),
    wisdom:         isSup ? ri(20, 60) : ri(0, 30),
    initiative:     ri(5, 30),
    dodge:          isDef ? ri(10, 40) : ri(0, 25),
    block:          isDef ? ri(10, 40) : ri(0, 25),
    elemental_resistances: [
      { element: 'Fire',    percentage: ri(-30, 60), flat: ri(0, 15) },
      { element: 'Earth',   percentage: ri(-30, 60), flat: ri(0, 15) },
      { element: 'Air',     percentage: ri(-30, 60), flat: ri(0, 15) },
      { element: 'Water',   percentage: ri(-30, 60), flat: ri(0, 15) },
      { element: 'Neutral', percentage: ri(-30, 60), flat: ri(0, 15) },
    ],
    spell_ids,
    ai_behavior: behavior,
    sprite,
  };
}

// ── Sprite picker ─────────────────────────────────────────────────────────────

/**
 * Visual grid that lets the admin associate a sprite sheet with a monster.
 * Shows a cropped preview of the sprite's first idle frame (row 0, col 0)
 * using CSS background clipping — no JS image loading needed.
 */
function SpritePicker({
  value,
  onChange,
  sprites,
}: {
  value:    string;
  onChange: (name: string) => void;
  sprites:  any[];
}) {
  // Size of the clipping box shown for each sprite card.
  const PREVIEW = 72; // px

  // The <img> is rendered at (PREVIEW * SPRITE_COLS) px wide and height:auto so
  // the browser preserves the natural aspect ratio of the sheet — no distortion.
  // overflow:hidden on the wrapper clips to the top-left frame (col 0, row 0).

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      {/* ── "None" option ── */}
      <button
        type="button"
        onClick={() => onChange('')}
        style={{
          display:      'flex',
          flexDirection:'column',
          alignItems:   'center',
          gap:           6,
          padding:      '8px 12px',
          borderRadius:  6,
          border:       `2px solid ${value === '' ? 'var(--accent)' : 'var(--border)'}`,
          background:    value === '' ? 'rgba(68,204,136,0.12)' : 'var(--surface)',
          cursor:        'pointer',
          minWidth:      80,
        }}
      >
        <div style={{
          width: PREVIEW, height: PREVIEW,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, background: 'var(--input-bg)', borderRadius: 4,
          color: 'var(--text-dim)',
        }}>
          ✕
        </div>
        <span style={{ fontSize: 11, color: value === '' ? 'var(--accent)' : 'var(--text-dim)', fontWeight: value === '' ? 700 : 400 }}>
          Nenhum
        </span>
      </button>

      {/* ── Sprite options ── */}
      {sprites.filter(s => s.type === 'monster').map((s) => {
        const fileId = (s.filename || s.name + '.png').replace('.png', '');
        const selected = value === fileId;
        return (
          <button
            key={s.name}
            type="button"
            onClick={() => onChange(fileId)}
            style={{
              display:       'flex',
              flexDirection: 'column',
              alignItems:    'center',
              gap:            6,
              padding:       '8px 12px',
              borderRadius:   6,
              border:        `2px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
              background:     selected ? 'rgba(68,204,136,0.12)' : 'var(--surface)',
              cursor:         'pointer',
              minWidth:       80,
            }}
            title={s.label}
          >
            {/*
              Sprite preview — first frame (col 0, row 0).
              The wrapper clips to PREVIEW×PREVIEW; the <img> is sized so that
              one column = PREVIEW px wide and height is auto (aspect-ratio
              preserved).  This avoids any distortion regardless of the sheet's
              actual pixel dimensions.
            */}
            <div style={{
              width:      PREVIEW,
              height:     PREVIEW,
              overflow:   'hidden',
              position:   'relative',
              borderRadius: 4,
              border:     '1px solid var(--border)',
              background: 'var(--input-bg)',
            }}>
              <img
                src={`/sprites/${fileId}.png?v=${Date.now()}`}
                alt={s.label}
                style={{
                  position:       'absolute',
                  top:            0,
                  left:           0,
                  width:          `calc(${PREVIEW}px * ${s.cols || 3})`,
                  height:         'auto',
                  imageRendering: 'pixelated',
                  display:        'block',
                }}
              />
            </div>
            <span style={{
              fontSize:   11,
              color:      selected ? 'var(--accent)' : 'var(--text-dim)',
              fontWeight: selected ? 700 : 400,
            }}>
              {s.icon} {s.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Reusable numeric field ────────────────────────────────────────────────────
function StatField({
  label,
  field,
  value,
  onChange,
  min = 0,
  hint,
}: {
  label: string;
  field: string;
  value: number;
  onChange: (field: string, v: number) => void;
  min?: number;
  hint?: string;
}) {
  return (
    <div className="formGroup">
      <label className="formLabel">{label}</label>
      <input
        type="number"
        className="formInput"
        value={value}
        min={min}
        onChange={(e) => onChange(field, Math.max(min, parseInt(e.target.value, 10) || 0))}
      />
      {hint && <span style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4, display: 'block' }}>{hint}</span>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function MonsterFormPage() {
  const { id }   = useParams<{ id: string }>();
  const isEdit   = Boolean(id);
  const navigate = useNavigate();
  const { t }    = useTranslation();

  const [form,    setForm]    = useState<MonsterTemplate>({ ...MONSTER_DEFAULTS });
  const [spells,  setSpells]  = useState<SpellData[]>([]);
  const [sprites, setSprites] = useState<any[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  // Load available spells and sprites
  useEffect(() => {
    void spellApi.list().then(setSpells).catch(() => {});
    void spriteApi.list().then(setSprites).catch(() => {});
  }, []);

  // Load monster to edit
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    void monsterApi.get(id!)
      .then((m) => setForm(m))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const setField = (field: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const setStatField = (field: string, value: number) => setField(field, value);

  const toggleSpell = (spellId: string) => {
    setForm((prev) => ({
      ...prev,
      spell_ids: prev.spell_ids.includes(spellId)
        ? prev.spell_ids.filter((s) => s !== spellId)
        : [...prev.spell_ids, spellId],
    }));
  };

  const setResistance = (index: number, field: 'percentage' | 'flat', value: number) => {
    setForm((prev) => {
      const arr = [...prev.elemental_resistances];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, elemental_resistances: arr };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError(t('monsterForm.nameRequired')); return; }

    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await monsterApi.update(id!, form);
      } else {
        await monsterApi.create(form);
      }
      navigate('/admin/monsters');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <>
      <div className="adminHeader"><h1>{t('common.loading')}</h1></div>
      <div className="adminContent"><div className="spinner" /></div>
    </>
  );

  return (
    <>
      <div className="adminHeader">
        <h1>{isEdit ? `✏ Edit: ${form.name}` : t('monsterForm.titleNew')}</h1>
        <button
          type="button"
          className="btn btnSecondary"
          onClick={() => setForm((prev) => ({ ...randomizeMonster(spells, sprites), id: prev.id }))}
          title={t('common.randomize')}
        >
          {t('common.randomize')}
        </button>
      </div>

      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}

        <form onSubmit={(e) => void handleSubmit(e)} className="adminForm">

          {/* ── Identity ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('monsterForm.identity')}</div>
            <div className="formGrid">
              <div className="formGroup formGroupFull">
                <label className="formLabel">{t('monsterForm.name')}</label>
                <input
                  type="text"
                  className="formInput"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder={t('monsterForm.namePlaceholder')}
                  required
                />
                <span style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4, display: 'block' }}>{t('monsterForm.hint.name')}</span>
              </div>
              <div className="formGroup formGroupFull">
                <label className="formLabel">{t('monsterForm.description')}</label>
                <textarea
                  className="formTextarea"
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder={t('monsterForm.descPlaceholder')}
                />
                <span style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4, display: 'block' }}>{t('monsterForm.hint.description')}</span>
              </div>
              <StatField label={t('monsterForm.level')} field="level" value={form.level} onChange={setStatField} min={1} hint={t('monsterForm.hint.level')} />
              <StatField label={t('monsterForm.xpReward')} field="xp_reward" value={form.xp_reward} onChange={setStatField} min={0} hint={t('monsterForm.hint.xpReward')} />
              {/* ── Pontuação de Ranking ── */}
              {/* Campo extra: pontos concedidos no ranking ao jogador que abater este monstro.
                  É SOMADO à regra global "monsterKill" definida no painel de Ranking.
                  Use 0 para monstros comuns. Para bosses/chefes, defina um valor alto (ex: 150). */}
              <div className="formGroup">
                <label className="formLabel" title="Pontos extras de ranking ao matar este monstro (somados à regra global)">
                  🏆 Pontos de Ranking (Bônus)
                </label>
                <input
                  type="number"
                  className="formInput"
                  min={0}
                  value={form.ranking_points}
                  onChange={(e) => setField('ranking_points', parseInt(e.target.value, 10) || 0)}
                  title="Pontos extras concedidos ao jogador que abater este monstro. 0 = usa apenas a regra global."
                />
                <span style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4, display: 'block' }}>
                  {t('monsterForm.hint.rankingBonus')}
                </span>
              </div>
              <div className="formGroup">
                <label className="formLabel">{t('monsterForm.aiBehavior')}</label>
                <select className="formSelect" value={form.ai_behavior}
                  onChange={(e) => setField('ai_behavior', e.target.value)}>
                  <option value="aggressive">{t('monsterForm.ai.aggressive')}</option>
                  <option value="defensive">{t('monsterForm.ai.defensive')}</option>
                  <option value="support">{t('monsterForm.ai.support')}</option>
                </select>
                <span style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4, display: 'block' }}>{t('monsterForm.hint.aiBehavior')}</span>
              </div>
              <div className="formGroup">
                <label className="formLabel">{t('monsterForm.hint.activeTitle')}</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    checked={form.active ?? true}
                    onChange={(e) => setField('active', e.target.checked)}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--text)' }}>
                    {t('monsterForm.hint.active')}
                  </span>
                </label>
              </div>
              <div className="formGroup">
                <label className="formLabel">{t('monsterForm.hint.dynamicLevelTitle')}</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    checked={form.is_dynamic_level ?? false}
                    onChange={(e) => setField('is_dynamic_level', e.target.checked)}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--text)' }}>
                    {t('monsterForm.hint.dynamicLevel')}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* ── Sprite ── */}
          <div className="adminCard">
            <div className="adminCardHeader">Sprite</div>
            <div style={{ padding: '14px 18px' }}>
              <p style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 12 }}>
                {t('monsterForm.hint.spriteDesc')}
              </p>
              <SpritePicker
                value={form.sprite}
                onChange={(name) => setField('sprite', name)}
                sprites={sprites}
              />
            </div>
          </div>

          {/* ── Turn Resources ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('monsterForm.resources')}</div>
            <div className="statsGrid">
              <StatField label={t('monsterForm.hpMax')} field="hp_max" value={form.hp_max} onChange={setStatField} min={1} hint={t('monsterForm.hint.hpMax')} />
              <StatField label={t('monsterForm.apMax')} field="ap_max" value={form.ap_max} onChange={setStatField} min={1} hint={t('monsterForm.hint.apMax')} />
              <StatField label={t('monsterForm.mpMax')} field="mp_max" value={form.mp_max} onChange={setStatField} hint={t('monsterForm.hint.mpMax')} />
            </div>
          </div>

          {/* ── Attributes ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('monsterForm.stats')}</div>
            <div className="statsGrid">
              <StatField label={t('monsterForm.strength')}     field="strength"     value={form.strength}     onChange={setStatField} hint={t('monsterForm.hint.strength')} />
              <StatField label={t('monsterForm.intelligence')} field="intelligence" value={form.intelligence} onChange={setStatField} hint={t('monsterForm.hint.intelligence')} />
              <StatField label={t('monsterForm.agility')}      field="agility"      value={form.agility}      onChange={setStatField} hint={t('monsterForm.hint.agility')} />
              <StatField label={t('monsterForm.luck')}         field="luck"         value={form.luck}         onChange={setStatField} hint={t('monsterForm.hint.luck')} />
              <StatField label={t('monsterForm.wisdom')}       field="wisdom"       value={form.wisdom}       onChange={setStatField} hint={t('monsterForm.hint.wisdom')} />
              <StatField label={t('monsterForm.initiative')}   field="initiative"   value={form.initiative}   onChange={setStatField} hint={t('monsterForm.hint.initiative')} />
              <StatField label={t('monsterForm.dodge')}        field="dodge"        value={form.dodge}        onChange={setStatField} hint={t('monsterForm.hint.dodge')} />
              <StatField label={t('monsterForm.block')}        field="block"        value={form.block}        onChange={setStatField} hint={t('monsterForm.hint.block')} />
            </div>
          </div>

          {/* ── Elemental Resistances ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('monsterForm.resistances')}</div>
            <div style={{ padding: '14px 18px' }}>
              <table className="resistTable">
                <thead>
                  <tr>
                    <th>{t('monsterForm.resist.element')}</th>
                    <th>{t('monsterForm.resist.percentage')}</th>
                    <th>{t('monsterForm.resist.flat')}</th>
                  </tr>
                </thead>
                <tbody>
                  {form.elemental_resistances.map((r, i) => (
                    <tr key={r.element}>
                      <td style={{ fontWeight: 600 }}>{r.element}</td>
                      <td>
                        <input type="number" className="resistInput"
                          value={r.percentage}
                          onChange={(e) => setResistance(i, 'percentage', parseInt(e.target.value, 10) || 0)} />
                      </td>
                      <td>
                        <input type="number" className="resistInput"
                          value={r.flat} min={0}
                          onChange={(e) => setResistance(i, 'flat', parseInt(e.target.value, 10) || 0)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8 }}>
                {t('monsterForm.resist.note')}
              </p>
            </div>
          </div>

          {/* ── Spells ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('monsterForm.spells')}</div>
            <p style={{ padding: '14px 18px 0', margin: 0, fontSize: 12, color: 'var(--text-dim)' }}>
              {t('monsterForm.hint.spellsDesc')}
            </p>
            <div className="spellChips">
              {spells.length === 0
                ? <span style={{ color: 'var(--text-dim)' }}>{t('monsterForm.spells.loading')}</span>
                : spells.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      className={`spellChip${form.spell_ids.includes(s.id) ? ' selected' : ''}`}
                      onClick={() => toggleSpell(s.id)}
                    >
                      <span style={{ fontWeight: 600 }}>{t(`spell.${s.id}.name` as any) || s.name}</span>
                      <span className="spellCost">{s.ap_cost} AP</span>
                    </button>
                  ))
              }
            </div>
          </div>

          {/* ── Actions ── */}
          <div className="formActions">
            <button type="submit" className="btn btnPrimary" disabled={saving}>
              {saving ? t('common.saving') : isEdit ? t('monsterForm.saveChanges') : t('monsterForm.create')}
            </button>
            <button type="button" className="btn btnSecondary"
              onClick={() => navigate('/admin/monsters')}>
              {t('common.cancel')}
            </button>
          </div>

        </form>
      </div>
    </>
  );
}
